import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Flame, CheckCircle2, Users, TreePine, Share2, Copy, Twitter, Download, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { createPledge, getRecentPledges, getImpact } from "@/lib/api";
import { downloadBadge } from "@/lib/badge";
import { getSource, captureSource } from "@/lib/source";
import { PINKSALE_URL } from "@/lib/links";
import { isPresaleLive, PRESALE_START_LABEL } from "@/lib/presale";
import { BuyPinkSaleButton } from "@/components/BuyPinkSaleButton";
import Countdown from "@/components/sections/Countdown";

const USD_PER_TREE = 1.25;

export default function Presale() {
  const live = isPresaleLive();
  const [form, setForm] = useState({ name: "", email: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [badge, setBadge] = useState({ name: "", trees: 0 });
  const [recent, setRecent] = useState([]);
  const [impact, setImpact] = useState(null);

  const loadRecent = useCallback(
    () => getRecentPledges().then(setRecent).catch((e) => console.error("Failed to load recent pledges:", e)),
    []
  );
  const loadImpact = useCallback(
    () => getImpact().then(setImpact).catch((e) => console.error("Failed to load impact stats:", e)),
    []
  );
  useEffect(() => { captureSource(); loadRecent(); loadImpact(); }, [loadRecent, loadImpact]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    const amount = form.amount ? parseFloat(form.amount) : 0;
    try {
      await createPledge({
        name: form.name.trim(),
        email: form.email.trim(),
        amount,
        source: getSource(),
      });
      setBadge({ name: form.name.trim(), trees: Math.floor(amount / USD_PER_TREE) });
      setDone(true);
      toast.success("You're on the list — welcome to the regrowth.");
      loadRecent();
      loadImpact();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const shareText = badge.trees > 0
    ? `I'm helping plant ${badge.trees.toLocaleString()} trees to rebuild forests after wildfires with AfterBurn ($AFTBRN). 🔥🌱 Join me:`
    : `I just joined the AfterBurn ($AFTBRN) movement to rebuild forests & communities after wildfires. 🔥🌱 Join me:`;
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";

  const shareX = () => {
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Copied — go spread the word!");
    } catch {
      toast.error("Couldn't copy. Please copy manually.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "AfterBurn $AFTBRN", text: shareText, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyShare();
    }
  };

  const saveImage = async () => {
    try {
      await downloadBadge({ name: badge.name, trees: badge.trees });
      toast.success("Badge image downloaded!");
    } catch {
      toast.error("Couldn't generate the image. Try again.");
    }
  };

  return (
    <section id="presale" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
      <div className="mb-16">
        <Countdown />
      </div>

      <div className="mb-16 flex justify-center" data-testid="pinksale-cta-wrap">
        {live ? (
          <BuyPinkSaleButton
            testId="presale-pinksale-btn"
            className="group inline-flex items-center justify-center gap-2 bg-ember text-black font-bold px-10 py-5 text-lg hover:bg-ember-light transition-colors"
          />
        ) : (
          <a
            href={PINKSALE_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="presale-verify-pinksale-link"
            className="inline-flex items-center gap-2 border border-white/15 px-6 py-3 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:border-ember hover:text-ember transition-colors"
          >
            <ShieldCheck className="w-4 h-4 text-forest-light" />
            Verify our presale on PinkSale — opens {PRESALE_START_LABEL}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-16 border border-white/10 bg-card p-8 sm:p-10"
        data-testid="presale-progress"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Presale Progress</span>
            <div className="mt-2 font-display font-bold text-3xl sm:text-4xl">
              ${Math.floor(impact?.raised || 0).toLocaleString()}
              <span className="text-muted-foreground text-xl sm:text-2xl">
                {" "}/ ${(impact?.goal || 250000).toLocaleString()}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-bold text-4xl sm:text-5xl text-ember" data-testid="presale-percent">
              {impact?.percent_funded ?? 0}%
            </div>
            <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Funded</div>
          </div>
        </div>
        <div className="mt-6 h-3 bg-background border border-white/10 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-forest-light via-ember to-ember-light"
            initial={{ width: 0 }}
            whileInView={{ width: `${impact?.percent_funded ?? 0}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            data-testid="presale-progress-bar"
          />
        </div>
        <p className="mt-4 font-mono text-xs text-muted-foreground">
          {(impact?.supporters || 0).toLocaleString()} supporters on the presale waitlist · minimum goal ${(impact?.goal || 250000).toLocaleString()}
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Become a Founding Supporter</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
            Claim your Founding Supporter badge.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Join the movement and instantly get your personal AfterBurn badge — showing the forest
            you're helping rebuild. You'll also get a launch-day alert the moment the presale opens
            Nov 12, so you never miss the 7-day window. Takes 20 seconds. No wallet, no payment.
          </p>

          <div className="mt-10 flex items-center gap-3 text-muted-foreground" data-testid="recent-supporters">
            <Users className="w-5 h-5 text-ember" />
            <span className="font-mono text-sm">
              {recent.length > 0
                ? `${recent[0].name} and others just joined`
                : "Be among the first to join"}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-card border border-white/10 p-8 sm:p-10 relative"
        >
          <div className="absolute -inset-px bg-gradient-to-b from-ember/30 to-transparent -z-10 blur-sm" />
          {done ? (
            <div className="text-center" data-testid="presale-success">
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-6 h-6" />
                <h3 className="font-display font-bold text-2xl">You're on the list!</h3>
              </div>
              <p className="mt-2 text-muted-foreground text-sm">
                Watch your inbox — presale details are on the way.
              </p>

              {/* Shareable impact badge */}
              <div
                data-testid="impact-badge"
                className="mt-6 relative overflow-hidden border border-ember/40 bg-gradient-to-br from-forest/30 via-background to-background p-8"
              >
                <div className="absolute -right-6 -top-6 opacity-10">
                  <TreePine className="w-40 h-40 text-emerald-400" />
                </div>
                <div className="relative">
                  <div className="flex items-center justify-center gap-2 text-ember mb-4">
                    <Flame className="w-4 h-4" />
                    <span className="font-mono text-xs uppercase tracking-[0.2em]">AfterBurn · $AFTBRN</span>
                  </div>
                  {badge.trees > 0 ? (
                    <>
                      <div className="font-display font-bold text-5xl sm:text-6xl text-emerald-400" data-testid="badge-tree-count">
                        {badge.trees.toLocaleString()}
                      </div>
                      <div className="mt-2 font-display font-bold text-xl text-foreground">
                        trees {badge.name ? `${badge.name} is` : "I'm"} helping plant 🌱
                      </div>
                    </>
                  ) : (
                    <div className="font-display font-bold text-2xl sm:text-3xl text-foreground">
                      {badge.name || "I"} joined the regrowth 🌱
                    </div>
                  )}
                  <p className="mt-3 font-mono text-xs text-muted-foreground">
                    Turning <span className="text-red-500">ashes</span> into <span className="text-emerald-400">forests</span>, one pledge at a time.
                  </p>
                </div>
              </div>

              <p className="mt-6 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Share your impact
              </p>
              <div className="mt-3 flex flex-wrap justify-center gap-3">
                <button
                  onClick={shareX}
                  data-testid="badge-share-x-btn"
                  className="inline-flex items-center gap-2 bg-ember text-black font-bold px-5 py-3 hover:bg-ember-light transition-colors"
                >
                  <Twitter className="w-4 h-4" /> Share on X
                </button>
                <button
                  onClick={nativeShare}
                  data-testid="badge-share-btn"
                  className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 hover:border-ember hover:text-ember transition-colors"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
                <button
                  onClick={copyShare}
                  data-testid="badge-copy-btn"
                  className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 hover:border-ember hover:text-ember transition-colors"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
                <button
                  onClick={saveImage}
                  data-testid="badge-download-btn"
                  className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 hover:border-ember hover:text-ember transition-colors"
                >
                  <Download className="w-4 h-4" /> Download
                </button>
              </div>

              <button
                onClick={() => { setDone(false); setForm({ name: "", email: "", amount: "" }); }}
                className="mt-6 text-muted-foreground hover:text-foreground text-sm transition-colors"
                data-testid="presale-reset-btn"
              >
                Add another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="presale-form">
              <div className="flex items-center gap-2 text-ember mb-2">
                <Flame className="w-5 h-5" />
                <span className="font-mono text-xs uppercase tracking-[0.2em]">Founding Supporter · $AFTBRN</span>
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Forest"
                  data-testid="presale-name-input"
                  className="mt-2 w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
                />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  data-testid="presale-email-input"
                  className="mt-2 w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
                />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Intended contribution (USD, optional)
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="100"
                  data-testid="presale-amount-input"
                  className="mt-2 w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="presale-submit-btn"
                className="w-full bg-ember text-black font-bold py-4 hover:bg-ember-light transition-colors disabled:opacity-60"
              >
                {submitting ? "Creating your badge..." : "Get My Founding Supporter Badge"}
              </button>
              <p className="font-mono text-[11px] text-muted-foreground text-center">
                No payment now, no wallet needed — just your badge, launch alerts, and early updates.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
